/**
 * Inline handoff offer (Figma Kin Chatbot frame `13372:48895`, "AI detects frustration and
 * auto-hands off"). Kin's message text carries the offer wording; this footer carries the
 * *actionable* control. Acceptance is the button being pressed with the offer's id (TDD 5.4):
 * it never depends on classifying a later free-text "yes", and because the offer is a persisted
 * message it stays acceptable after intervening turns.
 */

import { Box, Button, HStack, Text } from '@chakra-ui/react';

import { ADVISOR_ASSISTANT_BUBBLE_BG, ADVISOR_PANEL_NEUTRAL_200, ADVISOR_PANEL_PRIMARY_500 } from './advisor-panel-tokens';

/** Confirmed copy, frame `13372:48895`. */
export const SUPPORT_HANDOFF_ACCEPT_LABEL = 'Yes, Connect Me';
export const SUPPORT_HANDOFF_DECLINE_LABEL = 'Not Now';
/** Confirmed copy, frame `13372:49357` (TDD 4.2): shown the moment the offer is accepted. */
export const SUPPORT_HANDOFF_CONNECTING_COPY =
  "Connecting you to a support agent now. I'm sharing our conversation history so they'll have full context. Please hold on...";
/**
 * PLACEHOLDER: customer-facing copy for "the offer could not be created" is owned by design
 * (PS-1350, item "Error" state). Replace, do not polish.
 */
export const SUPPORT_HANDOFF_ERROR_PLACEHOLDER_COPY = 'We could not connect you to support just now. Please try again.';

export type SupportHandoffOfferState = 'idle' | 'pending' | 'accepted' | 'failed';

export interface SupportHandoffOfferCardProps {
  state: SupportHandoffOfferState;
  onAccept: () => void;
  onDecline: () => void;
  /** Blocks the controls without hiding the offer (e.g. no auth yet, or a stream is in flight). */
  disabled?: boolean;
}

export function SupportHandoffOfferCard({ state, onAccept, onDecline, disabled }: SupportHandoffOfferCardProps) {
  if (state === 'accepted') {
    // The agent persists the acceptance and its acknowledgement as real messages, and the panel
    // refetches on accept, so the transcript already says this. Rendering it here too showed the
    // copy twice — and above "Yes, Connect Me", implying we connected before the user agreed.
    // `pending` still covers the in-flight moment, so nothing is lost by collapsing here.
    return null;
  }
  const pending = state === 'pending';
  return (
    <Box
      w="100%"
      bg={ADVISOR_ASSISTANT_BUBBLE_BG}
      borderRadius="8px"
      px={3}
      pt={2}
      pb={3}
      data-testid="support-handoff-offer"
    >
      {state === 'failed' ? (
        <Text fontSize="sm" color="red.700" mb={2} role="alert">
          {SUPPORT_HANDOFF_ERROR_PLACEHOLDER_COPY}
        </Text>
      ) : null}
      <HStack gap={2} pt={2} borderTopWidth="1px" borderTopColor={ADVISOR_PANEL_NEUTRAL_200}>
        <Button
          type="button"
          size="sm"
          flex={1}
          variant="outline"
          bg="white"
          borderColor={ADVISOR_PANEL_NEUTRAL_200}
          color="fg"
          disabled={disabled || pending}
          onClick={onDecline}
        >
          {SUPPORT_HANDOFF_DECLINE_LABEL}
        </Button>
        <Button
          type="button"
          size="sm"
          flex={1}
          bg={ADVISOR_PANEL_PRIMARY_500}
          color="white"
          _hover={{ opacity: 0.9 }}
          disabled={disabled}
          loading={pending}
          loadingText={SUPPORT_HANDOFF_ACCEPT_LABEL}
          onClick={onAccept}
        >
          {SUPPORT_HANDOFF_ACCEPT_LABEL}
        </Button>
      </HStack>
    </Box>
  );
}
